import React from 'react';
import {connect} from 'react-redux';
import {dispatch} from 'redux';
import {Grid, Icon} from 'semantic-ui-react';
import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import lifecycle from 'recompose/lifecycle';
import {getResourcePageAction} from 'resources/actions';
import {createResourcePageItemsSelector} from 'resources/selectors';
import {toggleSortForResourceAction} from 'viewOptions/actions';
import {createResourceSortsSelector} from 'viewOptions/selectors';
import {startDeleteAction} from 'pages/DeleteConfirmation/actions';
import {collapseRow, expandRow} from './actions';
import * as ResourceApi from 'api/resources';
import {EmptyFunc, formatDate, isTruthy, SortDirection} from "utils/DBUtils";
import ItemTable from "components/common/ItemTable";
import {Link} from "react-router-dom";
import includes from 'lodash/includes';
import get from 'lodash/get';
import {Tooltip} from 'react-tippy';
import {FormattedMessage} from 'react-intl';
import {getUserResourceActions, getUserServiceActions, getUserBreakTypes} from 'pages/Profile/selectors';
import CreateBreakModalContainer from 'pages/BreakCreator/CreateBreakModalContainer';
import CopyBreakModalContainer from 'pages/BreakCopy/CopyBreakModalContainer';
import BreakListPagingBar from './BreakListPagingBar';
import BreakListFilterBar from './BreakListFilterBar';
import {startCopyChallengeAction} from "pages/BreakCopy/actions";
import {startPublishChallengeAction, startUnpublishChallengeAction} from "../BreakPublish/actions";
import PublishBreakModalContainer from "../BreakPublish/PublishBreakModalContainer";
import UnpublishBreakModalContainer from "../BreakPublish/UnpublishBreakModalContainer";
import ValidateBreakModalContainer from "../BreakPublish/ValidateBreakModalContainer";
import VerifyUnpublishChallengeModal from "../BreakPublish/VerifyUnpublishChallengeModal";
import {filterOptionsFactory} from 'viewOptions/factories';
import {refreshPageAction} from "../../resources/actions";
import {isNoU, UserGroups, RestrictedUserGroups} from "../../utils/DBUtils";

const getAnalyticsResourceActions = getUserResourceActions(ResourceApi.Types.Analytics);
const getBreakServiceActions = getUserServiceActions(ResourceApi.Types.Break);

const mapDispatchToProps = dispatch => {
  return {
      getChallenges: (filterOptions) => dispatch(getResourcePageAction(ResourceApi.Types.Challenge, 20, 1, null, filterOptions)),
      toggleSort: (fieldName) => dispatch(toggleSortForResourceAction(ResourceApi.Types.Challenge, fieldName)),
      expandRow: (row) => dispatch(expandRow(row)),
      collapseRow: (row) => dispatch(collapseRow(row)),
      showCopyBreakModal: (master) => dispatch(startCopyChallengeAction(master.id, master.permalink)),
      startDeleteChallengeAction: (challenge, brk) => dispatch(startDeleteAction(
          (brk.platform === "MASTER" ? challenge : brk), // send challenge if master so all platforms can be used to display in delete modal
          {type: ResourceApi.Types.Break, id: brk.id}, // resource id to delete
          [], // additional resource ids
          {}, // additional data
          [refreshPageAction(ResourceApi.Types.Challenge)] // post actions
      )),
      unpublishChallenge: (challenge, platform, brk) => dispatch(startUnpublishChallengeAction(challenge.id, platform, brk)),
      publishChallenge: (challenge, platform, brk) => dispatch(startPublishChallengeAction(challenge.id, platform, brk))
  };
};

const getSorts = createResourceSortsSelector(ResourceApi.Types.Challenge);
const getChallenges = createResourcePageItemsSelector(ResourceApi.Types.Challenge);

const mapStateToProps = state => {
  const sponsoredBreaksAllowed = includes(getUserBreakTypes(state), 'sponsored');

  const userCanViewAnalytics = includes(getAnalyticsResourceActions(state), 'READ');
  const userCanDeleteBreaks = includes(getBreakServiceActions(state), 'DELETE_BREAK');

  return {
      userCanViewAnalytics,
      userCanDeleteBreaks,
      sponsoredBreaksAllowed,
      getSortDirectionForField: field => getSorts(state)[field] || SortDirection.Unsorted,
      challenges: getChallenges(state.resources),
      currentExpandedRow: state.listBreaks.currentExpandedRow
  };
};

const BreakListTableConnected = connect(mapStateToProps, mapDispatchToProps);

const anyPlatformHasBeenLive = challenge => Object.keys(challenge.breaks).some(platform => {
  const brk = challenge.breaks[platform];

  // some breaks (mostly El Dailybreak?) don't have `hasBeenLive` set correctly, so triple-check to be sure.
  return brk.hasBeenLive || brk.publishStatus === 'LIVE' || brk.lastPublishedDate;
});

const BreakListTableWithProps = withProps((ownerProps) => ({
    getPermittedChallenges: () => {
      const filterOptions = ownerProps.sponsoredBreaksAllowed ? null : filterOptionsFactory({
        namedFilters: ['break_type_editorial']
      });

      return ownerProps.getChallenges(filterOptions);
    },
    items: ownerProps.challenges,
    onRowClicked: EmptyFunc,
    showSubRows: (challenge) => challenge.id === ownerProps.currentExpandedRow,
    showSubRow: (brk) => brk.platform !== 'MASTER',
    getSubRows: (challenge) => {
      //Since we want an the subrows to be in an order of W(dailybreak),D(demo),E(embed) like the rest of the app we have to specifically order them.
      let subRows = [];
      let breaks = challenge.breaks;

      //We have to check if each individual break exists.
      if (breaks.DAILYBREAK) {
        subRows.push(breaks.DAILYBREAK);
      }

      if (breaks.DEMO) {
        subRows.push(breaks.DEMO);
      }

      if (breaks.EMBED) {
        subRows.push(breaks.EMBED);
      }

      if (breaks.MASTER) {
        subRows.push(breaks.MASTER);
      }

      return subRows;
    },
    onSortClick: (details) => details.fieldName ? ownerProps.toggleSort(details.fieldName) : null,
    animatedSubRows: true,
    colDetails: [
        {
            header: "Break Url",
            fieldName: "permalink",
            detailFunc: (challenge) => <Link to={`/break/${challenge.id}/edit`}>{challenge.breaks.MASTER.permalink}</Link>,
            subRowDetailFunc: (platformBreak) => <div className="break-sub-row-text">{platformBreak.permalink}</div>,
            canSort: true,
            toolTip: true,
            toolTipData: (challenge) =>  challenge.breaks.MASTER.title,
            cellWidth: 8
        },
        {
            header: "Status",
            fieldName: "publishStatus",
            detailFunc: (challenge) => <div className={"tb-status-" + challenge.breaks.MASTER.publishStatus }>{ <FormattedMessage  id={"breakList.status_" + challenge.breaks.MASTER.publishStatus} /> }</div>,
            subRowDetailFunc: (platformBreak) => <div className={"tb-status-" + platformBreak.publishStatus }>{ <FormattedMessage  id={"breakList.status_" + platformBreak.publishStatus} /> }</div>,
            canSort: true,
            className: "status-column",
        },
        {
            header: "Modified",
            fieldName: "lastEdit",
            detailFunc: (challenge) => formatDate(challenge.breaks.MASTER.lastEdit, 'MM/DD/YY'),
            subRowDetailFunc: (platformBreak) => formatDate(platformBreak.lastEdit, 'MM/DD/YY'),
            canSort: true
        },
        {
            header: "Publish - End publish",
            fieldName: "startDate",
            detailFunc: (challenge) => `${formatDate(challenge.breaks.MASTER.startDate, 'MM/DD/YY')} - ${isTruthy(challenge.breaks.MASTER.endDate) ? formatDate(challenge.breaks.MASTER.endDate, 'MM/DD/YY') : ""}`,
            subRowDetailFunc: (platformBreak) => `${formatDate(platformBreak.startDate, 'MM/DD/YY')} - ${isTruthy(platformBreak.endDate) ? formatDate(platformBreak.endDate, 'MM/DD/YY') : ""}`,
            canSort: true
        },
        {
            header: "Type",
            detailFunc: (challenge) => <FormattedMessage  id={"breakList.type_" + challenge.breaks.MASTER.sponsoredType} />,
            subRowDetailFunc: (platformBreak) => <FormattedMessage  id={"breakList.type_" + platformBreak.sponsoredType} />
        },
        {
            header: "Author",
            detailFunc: (challenge) => get(challenge, 'breaks.MASTER.authors', []).length > 0 ? (challenge.breaks.MASTER.authors[0].username === "None" ? "N/A" : challenge.breaks.MASTER.authors[0].username) : "N/A",
            subRowDetailFunc: (platformBreak, challenge) => {
                let platformAuthor = platformBreak.authors ? platformBreak.authors[0] : null;
                if (isNoU(platformAuthor)) {
                    // use master author
                    platformAuthor = challenge.breaks.MASTER.authors ? challenge.breaks.MASTER.authors[0] : null;
                }
                return isNoU(platformAuthor) || (platformAuthor.username === "None") ? "N/A" : platformAuthor.username;
            },
            className: "actions-column"
        },
        {
            header: "Actions",
            detailFunc: (challenge) => {
                const isExpanded = ownerProps.currentExpandedRow === challenge.id;
                const toggleRowFn = isExpanded ? 'collapseRow' : 'expandRow';
                const toggleRowState = ownerProps[toggleRowFn].bind(null, challenge.id);
                const hasBeenLive = anyPlatformHasBeenLive(challenge);
                let platforms = Object.keys(challenge.breaks);
                return (
                  <Grid>
                    <Grid.Row>
                      <Grid.Column className="action-icon">
                        { (ownerProps.userCanDeleteBreaks && !hasBeenLive) ?
                          <Tooltip trigger="mouseenter" followCursor="true" title={'Delete Break'} className="testing-Popper">
                            <Icon name="trash alternate outline"  onClick={() => ownerProps.startDeleteChallengeAction(challenge, challenge.breaks.MASTER)}/>
                          </Tooltip>
                        : "" }
                      </Grid.Column>
                      <Grid.Column className="action-icon">
                        <Tooltip trigger="mouseenter" followCursor="true" title={'Copy Break'} className="testing-Popper">
                          <Icon name="copy" onClick={() => ownerProps.showCopyBreakModal(challenge.breaks.MASTER)}/>
                        </Tooltip>
                      </Grid.Column>
                      <Grid.Column className="action-icon">
                        { challenge.breaks.MASTER.sponsoredType !== "editorial" && Object.keys(challenge.breaks).length > 1 ?
                          <Tooltip trigger="mouseenter" followCursor="true" title={isExpanded ? 'Hide All' : 'View All'} className="testing-Popper">
                              <Icon name="eye"  onClick={toggleRowState}/>
                          </Tooltip>
                        : "" }
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                );
            },
            className: "action-column",
            subRowDetailFunc: (platformBreak, challenge) => {
              const hasBeenLive = anyPlatformHasBeenLive(challenge);
              return (
                <Grid>
                  <Grid.Row>
                    <Grid.Column>
                      {(ownerProps.userCanDeleteBreaks && !hasBeenLive) ?
                        <Tooltip trigger="mouseenter" followCursor="true" title={'Delete'} className="testing-Popper">
                          <Icon name="trash alternate outline"  onClick={() => ownerProps.startDeleteChallengeAction(challenge, platformBreak)}/>
                        </Tooltip>
                      : "" }
                    </Grid.Column>
                    <Grid.Column>

                    </Grid.Column>
                    <Grid.Column className="action-icon">

                    </Grid.Column>
                    <Grid.Column  className="action-icon">

                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              );
            },
            canSort: false
        }
    ]
}));

const BreakListTableWithLifeCycle = lifecycle({
    componentWillMount: function () {
        this.props.getPermittedChallenges();
    }
});

const BreakListTableComposed = compose(
    BreakListTableConnected,
    BreakListTableWithProps,
    BreakListTableWithLifeCycle
)(ItemTable);

const BreakListTableContainer = () => [
    <BreakListFilterBar key="BreakListFilterBar" />,
    <BreakListTableComposed key="BreakListTableComposed" />,
    <BreakListPagingBar key="BreakListPagingBar" />,
    <CreateBreakModalContainer key="CreateBreakModal"/>,
    <CopyBreakModalContainer key="CopyBreakModal"/>,
    <PublishBreakModalContainer key="PublishBreakModal" />,
    <UnpublishBreakModalContainer key="UnpublishBreakModal" />,
    <ValidateBreakModalContainer key="ValidateBreakModal" />,
    <VerifyUnpublishChallengeModal key="VerifyUnpublishChallengeModal"/>
];

export default BreakListTableContainer;



// WEBPACK FOOTER //
// ./public/pages/ListBreaks/BreakListTableContainer.jsx